import React, { useState, useEffect } from "react";
import { userAPI } from "../api/api";
import { Card, StatusBadge, Badge, PageHeader, EmptyState, Spinner, Button } from "../components/UI";
import { Search, Filter, Calendar, ChevronDown, ChevronUp } from "lucide-react";

function HistoryCard({ item, expanded, onToggle }) {
  const isHealthy = item.status?.toLowerCase() === "healthy";
  const isSick = item.status?.toLowerCase() === "sick";
  const bgBorder = isHealthy ? "border-brand-200" : isSick ? "border-red-200" : "border-earth-200";

  return (
    <div className={`bg-white rounded-2xl border-2 ${bgBorder} overflow-hidden shadow-sm hover:shadow-md transition-all`}>
      <div
        className="flex items-center gap-3 p-4 cursor-pointer"
        onClick={onToggle}
      >
        <div className="text-2xl w-10 text-center flex-shrink-0">
          {isHealthy ? "✅" : isSick ? "🚨" : "🔍"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-800 text-brand-800 truncate">{item.diagnosis}</div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <StatusBadge status={item.status} />
            <span className="text-xs text-brand-400 font-600 flex items-center gap-1">
              <Calendar size={11} />{new Date(item.analyzed_at).toLocaleString()}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="text-right">
            <div className="text-lg font-900 text-brand-700">{item.confidence}%</div>
            <div className="text-xs text-brand-400 font-600">confidence</div>
          </div>
          {expanded ? <ChevronUp size={18} className="text-brand-400" /> : <ChevronDown size={18} className="text-brand-400" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-0 border-t border-gray-100 bg-gray-50 animate-fade-in">
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <div>
              <div className="text-xs font-800 text-gray-400 uppercase tracking-wider mb-1">Description</div>
              <p className="text-sm font-600 text-gray-700 leading-relaxed">{item.description || "—"}</p>
            </div>
            {item.treatment && (
              <div className="bg-white rounded-xl p-3 border border-gray-200">
                <div className="text-xs font-800 text-gray-400 uppercase tracking-wider mb-1">💊 Treatment</div>
                <p className="text-sm font-700 text-gray-800 leading-relaxed">{item.treatment}</p>
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-3 flex-wrap text-xs">
            <div className="bg-white rounded-lg px-3 py-2 border border-gray-200">
              <span className="text-gray-400 font-700">Severity: </span>
              <span className="font-800 text-gray-800">{item.severity || "—"}</span>
            </div>
            <div className="bg-white rounded-lg px-3 py-2 border border-gray-200">
              <span className="text-gray-400 font-700">Species: </span>
              <span className="font-800 text-gray-800">{item.species_detected || "—"}</span>
            </div>
            <div className="bg-white rounded-lg px-3 py-2 border border-gray-200">
              <span className="text-gray-400 font-700">Result ID: </span>
              <span className="font-800 text-gray-800">{item.id?.slice(-10)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function HistoryPage() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    setLoading(true);
    userAPI.getHistory()
      .then((r) => { setResults(r.data.results || []); })
      .catch(() => setError("Could not load your history. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  const filtered = results.filter((r) => {
    const matchFilter = filter === "all" ||
      (filter === "healthy" && r.status?.toLowerCase() === "healthy") ||
      (filter === "sick" && r.status?.toLowerCase() === "sick") ||
      (filter === "review" && r.status?.toLowerCase() === "further review");
    const matchSearch = !search || r.diagnosis?.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const counts = {
    all: results.length,
    healthy: results.filter((r) => r.status?.toLowerCase() === "healthy").length,
    sick: results.filter((r) => r.status?.toLowerCase() === "sick").length,
    review: results.filter((r) => r.status?.toLowerCase() === "further review").length,
  };

  const FILTERS = [
    { id: "all", label: "All Results", emoji: "📋" },
    { id: "healthy", label: "Healthy", emoji: "✅" },
    { id: "sick", label: "Sick", emoji: "🚨" },
    { id: "review", label: "Needs Review", emoji: "🔍" },
  ];

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader title="📋 My Results" subtitle="All your plant and animal analyses" />

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl font-700 text-sm transition-all ${
              filter === f.id
                ? "bg-brand-600 text-white shadow-md"
                : "bg-white text-brand-600 border-2 border-brand-200 hover:border-brand-400"
            }`}
          >
            {f.emoji} {f.label}
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-800 ${filter === f.id ? "bg-white/30 text-white" : "bg-brand-100 text-brand-700"}`}>
              {counts[f.id]}
            </span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-brand-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by disease name..."
          className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-brand-200 bg-white text-brand-800 placeholder-brand-300 focus:outline-none focus:border-brand-500 font-600 text-base"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-4">
            <div className="text-5xl animate-bounce">🔬</div>
            <Spinner size={28} />
            <p className="text-brand-500 font-700">Loading your history...</p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 text-center">
          <div className="text-4xl mb-2">😢</div>
          <p className="text-red-700 font-700">{error}</p>
          <Button variant="secondary" className="mt-4" onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={results.length === 0 ? "🌱" : "🔍"}
          title={results.length === 0 ? "No analyses yet" : "No results found"}
          description={results.length === 0 ? "Go to the Dashboard and upload your first photo to get started!" : "Try changing your filter or search term"}
          action={results.length === 0 ? <a href="/dashboard"><Button>📸 Upload First Photo</Button></a> : null}
        />
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-brand-500 font-600">{filtered.length} result{filtered.length !== 1 ? "s" : ""} found</p>
          {filtered.map((item) => (
            <HistoryCard
              key={item.id}
              item={item}
              expanded={expandedId === item.id}
              onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
